/**
 * Classification of a query failure, stable enough to alert on.
 *
 * Without it every failure logs as `Error` and a timeout is indistinguishable
 * from a malformed query or a dead gateway.
 */
export type GraphqlFetchFailureReason =
  | 'ABORTED'
  | 'EMPTY_DATA'
  | 'GRAPHQL_ERROR'
  | 'HTTP_ERROR'
  | 'NETWORK'
  | 'UNKNOWN';

export class GraphqlFetchError extends Error {
  readonly reason: GraphqlFetchFailureReason;

  readonly operation: string;

  readonly status?: number;

  constructor(
    message: string,
    {
      reason,
      operation,
      status,
    }: { reason: GraphqlFetchFailureReason; operation: string; status?: number }
  ) {
    super(message);

    this.name = 'GraphqlFetchError';
    this.reason = reason;
    this.operation = operation;
    this.status = status;
  }
}

/** Maps anything thrown during a fetch onto a loggable reason code. */
export function classifyFetchFailure(error: unknown): GraphqlFetchFailureReason {
  if (error instanceof GraphqlFetchError) return error.reason;

  if (error instanceof Error) {
    // `AbortSignal.timeout()` rejects with `TimeoutError`; an explicit abort
    // rejects with `AbortError`. `fetch` itself rejects with `TypeError`.
    if (error.name === 'AbortError' || error.name === 'TimeoutError') return 'ABORTED';
    if (error.name === 'TypeError') return 'NETWORK';
  }

  return 'UNKNOWN';
}

/**
 * Flattens a cause to a primitive.
 *
 * Pino only applies its `err` serializer to the configured error keys, and
 * `JSON.stringify` of an `Error` yields `{}` because `message`, `name` and
 * `stack` are non-enumerable — so a nested cause has to be described by hand.
 */
function describeCause(cause: unknown): string | undefined {
  if (cause === undefined || cause === null) return undefined;
  if (cause instanceof Error) return `${cause.name}: ${cause.message}`;

  if (typeof cause === 'object') {
    try {
      return JSON.stringify(cause);
    } catch {
      // Circular or otherwise unserialisable — logging must never throw from
      // inside an error handler.
      return '[unserialisable cause]';
    }
  }

  return String(cause);
}

/**
 * Describes anything loggable as a type/message pair.
 *
 * Upstream GraphQL errors are plain `{ message }` objects, not `Error` instances,
 * and `String({ message })` yields `"[object Object]"` — so they need their own
 * branch or the message is lost.
 */
function describeError(error: unknown): { errorType: string; errorMessage: string } {
  if (error instanceof Error) return { errorType: error.name, errorMessage: error.message };

  if (typeof error === 'object' && error !== null && 'message' in error) {
    return { errorType: 'GraphQLError', errorMessage: String(error.message) };
  }

  return { errorType: typeof error, errorMessage: String(error) };
}

/**
 * Logs a query failure as structured fields.
 *
 * In production `next-logger` replaces `console.error` with pino's `logger.error`
 * (see its `lib/patches/console.js`), so pino's signature applies:
 * `(mergingObject, message)`. Passing the marker first would make it the message
 * and flatten every field into that string via `quick-format-unescaped`, leaving
 * nothing queryable — so the fields go first and the marker becomes the message.
 *
 * In development `console.error` is untouched, and this prints the object followed
 * by the marker. `event` is duplicated into the payload so the marker is greppable
 * in both environments.
 *
 * Field names deliberately avoid pino's reserved keys for this config —
 * `message` (its `messageKey`), `error` (its `errorKey`), `level` and `name` —
 * hence `errorMessage` rather than `message`.
 *
 * Request variables are never logged: they carry user identifiers and tokens, and
 * say nothing about why the query failed.
 *
 * `level` exists because not every logged event is an error. A GraphQL *partial*
 * response is a successful execution with some position nulled, and Brightspot
 * emits one for every missing page — so `HTZ_GRAPHQL_PARTIAL` is routine 404
 * traffic, not a fault. Logging it at `error` would bury the genuine
 * `HTZ_GRAPHQL_FETCH_FAILED` signal under crawler noise. Alert on partials as a
 * *ratio* of requests rather than an absolute count.
 */
export function logQueryFailure({
  event,
  operation,
  error,
  reason,
  status,
  path,
  level = 'error',
}: {
  event: 'HTZ_GRAPHQL_FETCH_FAILED' | 'HTZ_QUERY_FAILED' | 'HTZ_GRAPHQL_PARTIAL';
  operation: string;
  error: unknown;
  reason?: GraphqlFetchFailureReason;
  status?: number;
  /** Dot-joined GraphQL response position, when the failure has one. */
  path?: string;
  level?: 'error' | 'warn';
}): void {
  const resolvedStatus = status ?? (error instanceof GraphqlFetchError ? error.status : undefined);
  const cause = error instanceof Error ? describeCause(error.cause) : undefined;

  const fields = {
    event,
    operation,
    reason: reason ?? classifyFetchFailure(error),
    ...describeError(error),
    ...(resolvedStatus !== undefined ? { status: resolvedStatus } : {}),
    ...(path !== undefined ? { errorPath: path } : {}),
    ...(cause !== undefined ? { cause } : {}),
  };

  // Called directly rather than through a captured reference, which would lose its
  // `this`. Both keep pino's `(mergingObject, message)` signature.
  if (level === 'warn') {
    console.warn(fields, event);

    return;
  }

  console.error(fields, event);
}
